// Phase 3 documents (03 §3.4, §3.7, 13, 14 §14.9): trips, containers, exceptions, holds, alerts,
// devices, destination-cost advances and intercompany invoices. All server-written.
import { z } from 'zod';
import { branchCodeSchema, sectorCodeSchema } from './config';
import { barcodeSchema } from './cargo';

const minor = z.number().int().nonnegative().max(1e12);
const iso = z.string();

export const TRIP_STATUSES = ['loading', 'departed', 'receiving', 'closed'] as const;

/** `trips/{trp_…}`: a vehicle moving boxes between two branches. */
export const tripDocSchema = z.object({
  id: z.string(),
  kind: z.enum(['origin_internal', 'destination_internal']),
  fromBranchId: branchCodeSchema,
  toBranchId: branchCodeSchema,
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  vehicleId: z.string().optional(),
  /** The vehicle's plate as it was when the trip opened, so every screen can name the truck without reading vehicles. */
  vehiclePlate: z.string().optional(),
  driverName: z.string().default(''),
  status: z.enum(TRIP_STATUSES),
  counts: z
    .object({
      loaded: z.number().int(),
      received: z.number().int(),
      missing: z.number().int(),
      overage: z.number().int(),
    })
    .strict(),
  costMinor: minor.optional(),
  currency: z.string().optional(),
  stateSince: iso,
  nextDueAt: iso.optional(),
  createdAt: iso,
  departedAt: iso.optional(),
  closedAt: iso.optional(),
});

/** `trips/{id}/items/{barcode}` and `containers/{id}/items/{barcode}` */
export const carrierItemDocSchema = z.object({
  barcode: barcodeSchema,
  trackingNo: z.string(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  loadedAt: iso.optional(),
  loadedBy: z.string().optional(),
  receivedAt: iso.optional(),
  receivedBy: z.string().optional(),
  result: z.enum(['loaded', 'received', 'missing', 'overage', 'damaged', 'removed']),
});

export const CONTAINER_STATUSES = [
  'planned',
  'loading',
  'closed',
  'departing',
  'departed',
  'arrived_port',
  'pulled_out',
  'receiving',
  'devanned',
  'empty_returned',
  'completed',
] as const;
export const CUSTOMS_STATUSES = [
  'not_lodged',
  'lodged',
  'examination',
  'held',
  'released',
] as const;

export const freeTimeSchema = z
  .object({
    portFreeDays: z.number().int().min(0).max(60).default(4),
    demurrageFreeDays: z.number().int().min(0).max(60).default(4),
    detentionFreeDays: z.number().int().min(0).max(60).default(7),
    countWorkingDays: z.boolean().default(true),
    detentionStartsAt: z.enum(['pull_out', 'gate_out_full']).default('pull_out'),
    dailyRates: z
      .object({
        storageMinor: minor.optional(),
        demurrageMinor: minor.optional(),
        detentionMinor: minor.optional(),
        currency: z.string().optional(),
      })
      .strict()
      .default({}),
  })
  .strict();

/** `containers/{ctn_…}` (03 §3.7, 13 §13.1) */
/** One clock's cost as a command froze it: days over the last free day, and the money when a rate was known. */
const delayFrozenSchema = z.object({
  daysOver: z.number().int().nonnegative(),
  burnedMinor: z.number().int().nonnegative(),
  lastFreeDay: z.string().nullable(),
});

export const containerDocSchema = z.object({
  id: z.string(),
  kind: z.enum(['sea_fcl', 'sea_lcl', 'air']),
  containerType: z.enum(['40hc', '40ft', '20ft', 'lcl', 'air']),
  originSectorId: sectorCodeSchema,
  exportHubId: branchCodeSchema,
  destinationSectorId: sectorCodeSchema,
  importHubId: branchCodeSchema,
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  carrier: z.string().default(''),
  containerNo: z.string().default(''),
  sealNo: z.string().default(''),
  bookingNo: z.string().default(''),
  blNo: z.string().default(''),
  vessel: z.string().default(''),
  voyage: z.string().default(''),
  polPortId: z.string().default(''),
  podPortId: z.string().default('PHMNL'),
  etd: z.iso.date().optional(),
  eta: z.iso.date().optional(),
  dates: z
    .object({
      emptyPickedUp: iso.optional(),
      gateInAtPort: iso.optional(),
      closed: iso.optional(),
      atd: iso.optional(),
      blReleased: iso.optional(),
      ata: iso.optional(),
      discharged: iso.optional(),
      customsLodged: iso.optional(),
      customsReleased: iso.optional(),
      pulledOut: iso.optional(),
      arrivedAtHub: iso.optional(),
      devanned: iso.optional(),
      emptyReturned: iso.optional(),
    })
    .strict(),
  status: z.enum(CONTAINER_STATUSES),
  customs: z
    .object({
      status: z.enum(CUSTOMS_STATUSES),
      entryNo: z.string().default(''),
      notes: z.string().default(''),
    })
    .strict(),
  freeTime: freeTimeSchema,
  counts: z
    .object({
      loaded: z.number().int(),
      received: z.number().int(),
      missing: z.number().int(),
      overage: z.number().int(),
      shipments: z.number().int(),
    })
    .strict(),
  advanceId: z.string().optional(),
  costModelId: z.string().optional(),
  /** Where the empty went back, written by `returnEmptyContainer`. */
  depot: z.string().optional(),
  /**
   * Days over the free time, frozen by the command that stopped each clock: storage and demurrage
   * at the pull-out, detention at the empty return (13 §13.1).
   */
  delay: z
    .object({
      storage: delayFrozenSchema.optional(),
      demurrage: delayFrozenSchema.optional(),
      detention: delayFrozenSchema.optional(),
      zero: z.boolean().optional(),
      currency: z.string().nullable().optional(),
    })
    .optional(),
  stateSince: iso,
  nextDueAt: iso.optional(),
  /** Documents attached by `attachDocument` (03 §3.4a). */
  documentCount: z.number().int().nonnegative().optional(),
  lastDocumentAt: iso.optional(),
  createdAt: iso,
});

export const EXCEPTION_TYPES = [
  'missing',
  'overage',
  'damaged',
  'wrong_destination',
  'illegal_scan',
  'price_mismatch',
  'box_over_limit',
  'duplicate_customer',
  'stock_negative',
  'cash_short',
  'payment_dispute',
  'customs',
  'not_delivered_command',
  'invoice_blocked',
  'invoice_rejected',
  'ledger_mismatch',
  /** Money paid out or a bill recorded without its receipt: open until the receipt is attached (D-32). */
  'receipt_missing',
  'other',
] as const;

/** `exceptions/{exc_…}`: a work queue, not a log (03 §3.4). */
export const exceptionDocSchema = z.object({
  id: z.string(),
  type: z.enum(EXCEPTION_TYPES),
  severity: z.enum(['low', 'medium', 'high']),
  subject: z
    .object({
      kind: z.enum([
        'box',
        'shipment',
        'trip',
        'container',
        'till',
        'stock',
        'handover',
        'run',
        'driverCustody',
        'transfer',
        'bill',
        'command',
        'dayClose',
        'period',
        'ledger',
        'bankLine',
        'customer',
      ]),
      id: z.string(),
    })
    .strict(),
  /** A `duplicate_customer` names the customers that look alike (the new one first). */
  customerIds: z.array(z.string()).max(10).optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  raisedBy: z.string(),
  commandId: z.string().optional(),
  /** The trip or container whose scan raised it (a missing box is about the box, found through this). */
  carrier: z
    .object({ kind: z.enum(['trip', 'container']), id: z.string() })
    .strict()
    .optional(),
  description: z.string(),
  photos: z.array(z.string()).default([]),
  status: z.enum(['open', 'investigating', 'resolved', 'written_off']),
  assignee: z.string().optional(),
  resolution: z
    .object({ action: z.string(), note: z.string(), by: z.string(), at: iso })
    .strict()
    .optional(),
  stateSince: iso,
  createdAt: iso,
});

export const HOLD_TYPES = [
  'customs',
  'payment',
  'investigation',
  'customer_request',
  'address_issue',
] as const;

/** `alerts/{alr_…}` (13 §13.2) */
export const alertDocSchema = z.object({
  id: z.string(),
  clock: z.string(),
  subject: z.object({ kind: z.string(), id: z.string(), label: z.string() }).strict(),
  severity: z.enum(['at_risk', 'breached']),
  message: z.string(),
  ownerRole: z.string(),
  assignee: z.string().optional(),
  /** 1 = the owner, 2 = the manager, 3 = the directors (13 §13.2). */
  escalationLevel: z.number().int().min(1).max(3),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  dueAt: iso,
  moneyBurningPerDayMinor: minor.optional(),
  currency: z.string().optional(),
  raisedAt: iso,
  ackAt: iso.optional(),
  ackBy: z.string().optional(),
  actionNote: z.string().optional(),
  resolvedAt: iso.optional(),
  status: z.enum(['open', 'acknowledged', 'resolved']),
});

/** `devices/{deviceId}`: heartbeat used by day close and support (04 §4.6). */
export const deviceDocSchema = z.object({
  deviceId: z.string(),
  uid: z.string(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  app: z.enum(['office', 'ops']),
  appVersion: z.string(),
  pendingCount: z.number().int().nonnegative(),
  lastSyncAt: iso,
  /** When the server heard from the device: the unsynced-device check on `closeDay` counts from it. */
  reportedAt: iso.optional(),
});

/** `advances/{adv_…}` (14 §14.9): destination costs sent ahead of the ship. */
export const advanceDocSchema = z.object({
  id: z.string(),
  containerId: z.string(),
  fromEntityId: sectorCodeSchema,
  toEntityId: sectorCodeSchema,
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  estimate: z.array(z.object({ currency: z.string(), amountMinor: minor }).strict()),
  dueDate: z.iso.date(),
  status: z.enum(['requested', 'scheduled', 'funded', 'applied']),
  funded: z
    .array(
      z
        .object({
          sentMinor: minor,
          sentCurrency: z.string(),
          receivedMinor: minor,
          receivedCurrency: z.string(),
          at: iso,
        })
        .strict(),
    )
    .default([]),
  createdAt: iso,
});

/** `intercompanyInvoices/{ici_…}` (14 §14.9) */
export const intercompanyInvoiceDocSchema = z.object({
  id: z.string(),
  containerId: z.string(),
  fromEntityId: sectorCodeSchema,
  toEntityId: sectorCodeSchema,
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  lines: z.array(
    z
      .object({
        item: z.string(),
        amountMinor: minor,
        currency: z.string(),
        /** The cost account the line recharges (06 §6.2); older invoices have none. */
        accountCode: z.string().optional(),
      })
      .strict(),
  ),
  appliedAdvanceId: z.string().optional(),
  /** `reversed`: a TEST invoice undone on staging (`reverseTestIntercompanyInvoice`, 2026-10-30). */
  status: z.enum(['open', 'settled', 'reversed']),
  /** How it was priced (14 §14.9 policy): the cost-model estimate or the actual costs. */
  basis: z.enum(['cost_model', 'actual']).optional(),
  /**
   * What is left open after the advance, in the destination's currency, with the same balance as the
   * origin's books carry it (`originBookMinor`, origin currency). `owed`: the origin pays it;
   * `surplus`: the destination refunds it. Settlements and true-ups move it; zero is settled.
   */
  balance: z
    .object({
      side: z.enum(['owed', 'surplus', 'none']),
      currency: z.string(),
      originCurrency: z.string(),
      destMinor: minor,
      originBookMinor: minor,
    })
    .strict()
    .optional(),
  /** What has been recharged per cost account so far (destination currency), for the true-up. */
  recharged: z.record(z.string(), z.number().int()).optional(),
  settlements: z
    .array(
      z.object({
        payer: z.enum(['origin', 'destination']),
        amountMinor: minor,
        sentMinor: minor,
        sentCurrency: z.string(),
        receivedMinor: minor,
        receivedCurrency: z.string(),
        originFxMinor: z.number().int(),
        destinationFxMinor: z.number().int(),
        at: iso,
        journalIds: z.array(z.string()),
      }),
    )
    .optional(),
  trueUps: z
    .array(
      z.object({
        source: z.string(),
        deltaMinor: z.number().int(),
        at: iso,
        journalIds: z.array(z.string()),
      }),
    )
    .optional(),
  pendingSettlementApprovalId: z.string().optional(),
  /** Who reversed a TEST invoice, why, and the reversing journals. */
  reversal: z
    .object({ journalIds: z.array(z.string()), reason: z.string(), by: z.string(), at: iso })
    .strict()
    .optional(),
  createdAt: iso,
});

/** `intercompanyTransfers/{ict_…}` (14 §14.9): money between two countries that is not an advance. */
export const intercompanyTransferDocSchema = z.object({
  id: z.string(),
  /** The sending country and the receiving one. */
  fromEntityId: sectorCodeSchema,
  toEntityId: sectorCodeSchema,
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  purpose: z.enum(['working_capital', 'cost_cover', 'loan', 'repayment', 'other']),
  reason: z.string(),
  fromWalletId: z.string(),
  toWalletId: z.string(),
  sentMinor: minor,
  sentCurrency: z.string(),
  receivedMinor: minor,
  receivedCurrency: z.string(),
  status: z.enum(['awaiting_approval', 'done', 'rejected']),
  approvalId: z.string().optional(),
  journalIds: z.array(z.string()).default([]),
  makerUid: z.string(),
  approvedBy: z.string().optional(),
  createdAt: iso,
  doneAt: iso.optional(),
});

// ---- The cash calendar and the reserves (14 §14.6, §14.8) --------------------------------------
// Both are server documents. The hourly job writes `cashCalendar/{entityId}` and the payment
// commands keep `reserveTotals/{entityId}__{currency}`; the office only reads them.

const isoDate = z.iso.date();

export const CASH_ENTRY_KINDS = [
  'container',
  'bill',
  'running',
  'advance',
  'receipt',
  'remittance',
  'transit',
] as const;

/**
 * One dated line of the forecast, with how it is covered (`projectCashCalendar`, domain): a cost of
 * a container, a supplier bill, the month's running costs, an advance, or money expected in (a
 * shipment balance, a partner's remittance, cash in tills and card settlements on its way).
 */
export const cashEntrySchema = z
  .object({
    id: z.string(),
    kind: z.enum(CASH_ENTRY_KINDS).default('container'),
    direction: z.enum(['in', 'out']).default('out'),
    containerId: z.string().optional(),
    containerLabel: z.string().optional(),
    item: z.string(),
    label: z.string(),
    entityId: sectorCodeSchema,
    currency: z.string(),
    amountMinor: minor,
    dueDate: isoDate,
    coveredByWalletMinor: minor,
    coveredByReservesMinor: minor,
    uncoveredMinor: minor,
    /** A date or amount the calendar assumed (a container still loading, a budget carried forward). */
    projected: z.boolean().optional(),
    /** Between two EcoBox entities (an advance): left out of the group view. */
    intercompany: z.boolean().optional(),
    ref: z.object({ kind: z.string(), id: z.string() }).strict().optional(),
  })
  .strict();

/** A cost the paying entity's own cash does not cover: money has to be sent in by `sendByDate`. */
export const cashShortfallSchema = z
  .object({
    needId: z.string(),
    containerId: z.string().optional(),
    /** Not a container cost: a bill, running costs or an advance the entity's cash does not cover. */
    source: z.enum(CASH_ENTRY_KINDS).optional(),
    item: z.string(),
    entityId: sectorCodeSchema,
    currency: z.string(),
    dueDate: isoDate,
    sendByDate: isoDate,
    needMinor: minor,
    coveredByWalletMinor: minor,
    coveredByReservesMinor: minor,
    uncoveredMinor: minor,
    transferMinor: minor,
    kind: z.enum(['transfer_needed', 'uncovered']),
    reservesEntityId: sectorCodeSchema.optional(),
  })
  .strict();

/** One week of one currency: cash at the start, costs falling due, cash at the end. */
export const cashWeekSchema = z
  .object({
    entityId: sectorCodeSchema,
    currency: z.string(),
    weekStart: isoDate,
    weekEnd: isoDate,
    openingMinor: z.number().int(),
    inMinor: minor,
    outMinor: minor,
    closingMinor: z.number().int(),
    negative: z.boolean(),
  })
  .strict();

/** Per currency: what is still to pay, the cash held, the reserves earmarked for these containers. */
export const cashSummarySchema = z
  .object({
    currency: z.string(),
    requiredMinor: minor,
    walletMinor: z.number().int(),
    reservesHeldMinor: minor,
    freeCashMinor: z.number().int(),
    coveragePercent: z.number().int().nonnegative(),
    /** Receipts expected in the forecast (not counted in the coverage). */
    receiptsMinor: minor.default(0),
  })
  .strict();

/** 7, 30 and 90 days of one currency: money in, money out, by kind, and what cash does not cover. */
export const cashWindowSchema = z
  .object({
    days: z.number().int().positive(),
    currency: z.string(),
    inMinor: minor,
    outMinor: minor,
    netMinor: z.number().int(),
    outByKind: z.partialRecord(z.enum(CASH_ENTRY_KINDS), minor),
    inByKind: z.partialRecord(z.enum(CASH_ENTRY_KINDS), minor),
    shortByMinor: minor,
    uncoveredMinor: minor,
  })
  .strict();

/** One entity in the group view (`cashCalendar/GROUP`), converted to AED. */
export const cashGroupEntitySchema = z
  .object({
    entityId: sectorCodeSchema,
    currency: z.string(),
    /** One unit of the entity's currency in AED (decimal text), or null when no rate converts it. */
    aedRate: z.string().nullable(),
    windows: z.array(cashWindowSchema),
  })
  .strict();

/** `cashCalendar/{entityId}`: written hourly by `runCashCalendar`, read by the Plan screen. */
export const cashCalendarDocSchema = z.object({
  entityId: sectorCodeSchema,
  today: isoDate,
  updatedAt: iso,
  entries: z.array(cashEntrySchema).default([]),
  weeks: z.array(cashWeekSchema).default([]),
  shortfalls: z.array(cashShortfallSchema).default([]),
  summary: z.array(cashSummarySchema).default([]),
  /** Costs left out because no FX rate converts them into the entity's currency. */
  unconverted: z.array(z.string()).default([]),
  /** 7, 30 and 90 days per currency (the group document: in AED). */
  windows: z.array(cashWindowSchema).default([]),
  /** The group document only: each entity's windows in AED. */
  entities: z.array(cashGroupEntitySchema).default([]),
  /** Unpaid balances older than 90 days, left out of the forecast as doubtful, per currency. */
  doubtful: z.array(z.object({ currency: z.string(), amountMinor: minor }).strict()).default([]),
  /** What the job read, and whether a cap cut it short (then the figures are a floor). */
  limits: z
    .object({
      containers: z.number().int().nonnegative(),
      truncated: z.array(z.string()),
      entriesOmitted: z.number().int().nonnegative().default(0),
    })
    .optional(),
});

const bucketTotalsSchema = z.object({
  allocatedMinor: z.number().int().optional(),
  releasedMinor: z.number().int().optional(),
});

/** `reserveTotals/{entityId}__{currency}`: allocations and releases summed per origin entity and currency. */
export const reserveTotalsDocSchema = z.object({
  entityId: sectorCodeSchema,
  currency: z.string(),
  allocatedMinor: z.number().int().default(0),
  releasedMinor: z.number().int().default(0),
  buckets: z.record(z.string(), bucketTotalsSchema).default({}),
  /**
   * The running-cost reserve month by month (14 §14.6): set aside for the month, released by the
   * running costs paid in it, every running cost paid in it, and what month end gave back.
   */
  runningMonths: z
    .record(
      z.string(),
      z.object({
        allocatedMinor: z.number().int().optional(),
        releasedMinor: z.number().int().optional(),
        paidMinor: z.number().int().optional(),
        closedOutMinor: z.number().int().optional(),
        closedOutAt: iso.optional(),
      }),
    )
    .default({}),
  updatedAt: iso,
});
